import { Link } from "react-router-dom";
import { Shield, Check, Phone, Star, MapPin, Bug, Bird, Home, ChevronRight, Clock } from "lucide-react";
import { Logo, Header } from "../components/Layout";
import ServiceAreaMap from "../components/ServiceAreaMap";
import { SERVICE_AREAS_MAP } from "../data/serviceAreas";
import { IPSWICH_SUBURB_LINKS } from "../data/ipswichSuburbs";

const AREA = SERVICE_AREAS_MAP["ipswich"];

const services = [
  { title: "Pest Control", desc: "Cockroaches, ants, spiders, rodents & more. Same-day service with 6-12 month warranty.", path: "/pest-control-ipswich", color: "#E74C3C", icon: Bug },
  { title: "Cockroach Treatment", desc: "Targeted cockroach elimination for German, American & Australian species. All life stages treated.", path: "/cockroach-treatment-ipswich", color: "#E74C3C", icon: Bug },
  { title: "Termite Inspection", desc: "Thermal imaging & moisture detection. Detailed reports with photos. Catch termites before they cause damage.", path: "/termite-inspection-ipswich", color: "#D2691E", icon: Shield },
  { title: "Termite Treatment", desc: "Chemical barriers & baiting systems to protect your home. 5-8 year protection with warranty.", path: "/termite-treatment-ipswich", color: "#D2691E", icon: Shield },
  { title: "Bird Control", desc: "Solar panel bird proofing, pigeon removal, bird netting & mite treatment. 10-year mesh warranty.", path: "/bird-control-ipswich", color: "#5BC0DE", icon: Bird },
  { title: "Solar Bird Proofing", desc: "Stainless steel mesh installed around solar panels. Stop pigeons nesting & damaging your system.", path: "/bird-proofing-ipswich", color: "#5BC0DE", icon: Bird },
  { title: "End of Lease", desc: "Bond-back pest treatment with certificate. Same-day booking available. Accepted by all agents.", path: "/end-of-lease-pest-control-ipswich", color: "#7ABC32", icon: Home },
  { title: "Affordable Pest Control", desc: "Professional treatments from $165. No hidden fees, no franchise costs. Same quality, lower price.", path: "/cheap-pest-control-ipswich", color: "#7ABC32", icon: Shield },
];

const suburbs = ["Ipswich CBD", "Brassall", "Booval", "Bundamba", "Goodna", "Redbank Plains", "Collingwood Park", "Bellbird Park", "Ripley", "Springfield", "North Ipswich", "East Ipswich", "Leichhardt", "Silkstone", "Yamanto", "Karalee", "Raceview", "Woodend", "One Mile", "Sadliers Crossing"];

export default function IpswichHubPage() {
  return (
    <>
      <>
        <title>Pest Control Services Ipswich QLD | Pest Free Ipswich & Brisbane</title>
        <meta name="description" content="Complete pest control services in Ipswich QLD. Cockroaches, termites, birds, end of lease & more. QBCC licensed, locally owned. Same-day service. Get an instant quote." />
        <link rel="canonical" href="https://pestfreebrisbane.com.au/ipswich" />
      </>

      <Header />
      <div className="min-h-screen bg-slate-50">
        {/* Hero */}
        <section className="bg-gradient-to-br from-slate-900 via-[#1a2332] to-[#7ABC32]/15 text-white pt-28 pb-16 md:pt-32 md:pb-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#7ABC32]/20 border border-[#7ABC32]/30 rounded-full px-4 py-1.5 mb-5">
                  <MapPin className="w-4 h-4 text-[#7ABC32]" />
                  <span className="text-sm font-medium text-[#7ABC32]">Ipswich QLD</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight" data-testid="ipswich-hub-h1">
                  Pest Control Services <span className="text-[#7ABC32]">Ipswich</span>
                </h1>
                <p className="text-lg text-slate-300 mb-6">
                  Your local Ipswich pest control experts. From cockroaches and termites to bird proofing and end-of-lease treatments — we cover every pest problem across every Ipswich suburb. QBCC licensed, fully insured, and locally owned.
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />Same Day Service</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />QBCC Licensed</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />Locally Owned</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />Fully Insured</div>
                </div>
                <div className="flex items-center gap-3">
                  <a href="tel:0737081422" className="flex items-center gap-2 bg-[#7ABC32] hover:bg-[#6AAD28] text-white font-semibold px-6 py-3 rounded-full transition-all" data-testid="ipswich-hub-call-btn">
                    <Phone className="w-5 h-5" /> (07) 3708 1422
                  </a>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                    <span className="text-sm text-slate-300 ml-1">4.9 on Google</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <Logo className="h-32 md:h-40 opacity-90" />
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-14 md:py-20">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800">Our Ipswich Services</h2>
              <p className="text-slate-600 mt-2 max-w-2xl mx-auto">Choose a service below to learn more and get an instant quote</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="services-grid">
              {services.map((s, i) => (
                <Link key={i} to={s.path} className="group bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:border-slate-300 transition-all" data-testid={`service-card-${i}`}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: s.color + '12' }}>
                    <s.icon className="w-5 h-5" style={{ color: s.color }} />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2 group-hover:text-[#7ABC32] transition-colors">{s.title}</h3>
                  <p className="text-sm text-slate-600 mb-3">{s.desc}</p>
                  <span className="inline-flex items-center text-sm font-semibold text-[#7ABC32] group-hover:gap-2 transition-all">
                    Get Quote <ChevronRight className="w-4 h-4 ml-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Why Ipswich Chooses Pest Free</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: "Local to Ipswich", desc: "We're based in the Ipswich region — not a Brisbane company doing a long drive. We know the local pest pressures, soil types and housing styles that affect treatment.", color: "#7ABC32" },
                { title: "Honest, Upfront Pricing", desc: "Our online calculators show you the price before you book. No call-out fees, no hidden charges. What you see is what you pay.", color: "#5BC0DE" },
                { title: "Fast Response", desc: "Same-day and next-day service across all Ipswich suburbs. We prioritise urgent jobs like active termites, end-of-lease deadlines and bird mite infestations.", color: "#E74C3C" },
              ].map((item, i) => (
                <div key={i} className="text-center p-6 rounded-2xl bg-slate-50 border border-slate-100">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: item.color + '12' }}>
                    <Check className="w-6 h-6" style={{ color: item.color }} />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Area Map */}
        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-3">Our Ipswich Service Area</h2>
            <p className="text-slate-600 mb-8 max-w-2xl mx-auto">The shaded green area below shows the suburbs we regularly service across the Ipswich region.</p>
            <ServiceAreaMap
              areaKey="ipswich"
              center={AREA.center}
              zoom={AREA.zoom}
              polygon={AREA.polygon}
              suburbs={AREA.suburbs}
            />
          </div>
        </section>

        {/* Suburbs */}
        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Serving All Ipswich Suburbs</h2>
            <div className="flex flex-wrap justify-center gap-2" data-testid="ipswich-suburbs-grid">
              {suburbs.map(s => {
                const link = IPSWICH_SUBURB_LINKS[s];
                const slug = s.toLowerCase().replace(/\s+/g, '-');
                return link ? (
                  <Link
                    key={s}
                    to={link}
                    data-testid={`suburb-link-${slug}`}
                    className="bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700 hover:border-[#7ABC32] hover:bg-[#7ABC32]/5 hover:text-[#7ABC32] transition-all"
                  >
                    {s}
                  </Link>
                ) : (
                  <span key={s} className="bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700">{s}</span>
                );
              })}
            </div>
            <p className="text-slate-500 text-sm mt-4">And all surrounding areas in the Ipswich region</p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-gradient-to-r from-slate-900 to-[#7ABC32] text-white text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ipswich's Local Pest Experts</h2>
            <p className="text-lg text-white/80 mb-6">Choose a service above for an instant quote, or call us now for same-day service.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:0737081422" className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold px-8 py-3 rounded-full hover:bg-slate-100 transition-all">
                <Phone className="w-5 h-5" /> Call (07) 3708 1422
              </a>
              <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="inline-flex items-center gap-2 border-2 border-white text-white font-bold px-8 py-3 rounded-full hover:bg-white/10 transition-all">
                <Clock className="w-5 h-5" /> View Services
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
